import { yupResolver } from '@hookform/resolvers/yup';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import { usePhoneOptionsContext } from '../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../types/api';
import {
  CouriersDevices,
  SmartPOSProviders,
  SmartPOSTerminals,
} from '../../../../../types/api/smartPosTerminals';
import { debounceTimeout } from '../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../utils/constants/phoneNumbers';
import { queryString } from '../../../../../utils/helpers/http';
import { getChangedValues } from '../../../../../utils/helpers/object';
import {
  formatMobileNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../utils/helpers/phoneNumbers';
import AutoCompleteInput from '../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import {
  FormFields,
  getDefaultValues,
  getValidationSchema,
  smartPOSTerminalsStatuses,
} from './AddEditDialog.functions';

type Props = {
  isEditDialog: boolean;
  data: SmartPOSTerminals | undefined;
  handleCreation: (data: FormFields) => void;
  handleEditing: (data: any) => void;
};

function FormContainer({
  isEditDialog,
  data,
  handleCreation,
  handleEditing,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const options = useMemo(() => smartPOSTerminalsStatuses(t), [t]);

  const defaultValues = useMemo(
    () => getDefaultValues(isEditDialog, data),
    [isEditDialog, data]
  );

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const methods = useForm<FormFields>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, reset, getValues, setValue } = methods;

  const courierName = useWatch<FormFields, 'courierName'>({
    control: methods.control,
    name: 'courierName',
  });

  const debouncedCourierFilter = useDebounce(courierName, debounceTimeout);

  const courierId = useWatch({ control: methods.control, name: 'courierId' });

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const { data: devicesCourier } = useAxiosHook<WithData<CouriersDevices[]>>({
    url:
      '/couriers-devices' +
      queryString({
        employeeName: debouncedCourierFilter,
        limit: 0,
      }),
  });

  const { data: smartPOSProvidersData } = useAxiosHook<SmartPOSProviders[]>({
    url: '/v2/smart-pos-terminal-providers',
  });

  const couriersDeviceOptions = useMemo(
    () =>
      devicesCourier?.data.map((item) => ({
        label: item.employeeName,
        value: item.employeeId,
      })) ?? [],
    [devicesCourier]
  );

  const devicesByCourierOptions = useMemo(
    () =>
      courierId
        ? devicesCourier?.data
            .filter((courier) => courier.employeeId === courierId)[0]
            ?.devices?.map((item: any) => ({
              label: item.deviceNameId,
              value: item.id,
            }))
        : [],
    [courierId, devicesCourier]
  );

  const smartPOSProviderOptions = useMemo(
    () =>
      smartPOSProvidersData?.length
        ? smartPOSProvidersData.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
        : [],
    [smartPOSProvidersData]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  function handleFormSubmission(values: FormFields) {
    if (isEditDialog) {
      handleEditing(getChangedValues(values, defaultValues));
      return;
    }

    handleCreation(values);
  }

  return (
    <FormProvider {...methods}>
      <form
        id="smart-pos-terminals-create-edit-form"
        onSubmit={handleSubmit(handleFormSubmission)}
      >
        <div className="p-fluid">
          <FieldWithErrors name="terminalName" label={t('Terminal name')}>
            <Controller
              name="terminalName"
              render={({ field }) => (
                <InputText
                  id="terminalName"
                  name="terminalName"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="courierId" label={t('Courier')}>
            <Controller
              name="courierId"
              render={({ field }) => (
                <AutoCompleteInput
                  placeholder={t('Search')}
                  options={couriersDeviceOptions}
                  value={field.value}
                  filterValue={courierName}
                  onFilterChange={(e) => setValue('courierName', e)}
                  onSelectionChange={field.onChange}
                  filterDataCy="courierId"
                  optionsClassName="data-cy-courier_id-options"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="smartPosProviderId"
            label={t('Smart POS Provider')}
          >
            <Controller
              name="smartPosProviderId"
              render={({ field }) => (
                <Dropdown
                  id="smartPosProviderId"
                  name="smartPosProviderId"
                  options={smartPOSProviderOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!Boolean(smartPOSProviderOptions?.length)}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="deviceId" label={t('Device ID')}>
            <Controller
              name="deviceId"
              render={({ field }) => (
                <Dropdown
                  id="deviceId"
                  name="deviceId"
                  options={devicesByCourierOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!Boolean(devicesByCourierOptions?.length)}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="terminalId" label={t('Terminal ID')}>
            <Controller
              name="terminalId"
              render={({ field }) => (
                <InputText
                  id="terminalId"
                  name="terminalId"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="activationCode" label={t('Activation Code')}>
            <Controller
              name="activationCode"
              render={({ field }) => (
                <InputText
                  id="activationCode"
                  name="activationCode"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="pin" label={t('Pin')}>
            <Controller
              name="pin"
              render={({ field }) => (
                <InputText
                  id="pin"
                  name="pin"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="mobileNumber" label={t('Mobile')}>
            <div className="p-inputgroup">
              <Controller
                name="mobileNumberRegion"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="mobileNumberRegion"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="mobileNumberRegion"
                    {...field}
                  />
                )}
              />
              <Controller
                name="mobileNumber"
                render={({ field }) => (
                  <InputText
                    name="mobileNumber"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      field.value,
                      getValues()?.mobileNumberRegion ||
                        getPhoneorMobileNumberRegion(field.value)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    className="data-cy-mobile"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="statusId" label={t('Status')}>
            <Controller
              name="statusId"
              render={({ field }) => (
                <Dropdown
                  id="statusId"
                  name="statusId"
                  options={options}
                  value={String(field.value)}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>
        </div>
      </form>
    </FormProvider>
  );
}

export default FormContainer;
